import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Play } from "lucide-react";
import api from "../../lib/apiClient";

// FollowingRecentStrip — top-of-`/feed` activity bar showing the most
// recent videos from creators the user follows. Last 24h only.
//
// Why a strip vs. inlining into the player overlay:
//   - Gives `/feed` a meaningfully different experience from `/`.
//     Without something distinctive, MyFeed is just "home with the
//     toggle pre-set" and there's no reason to bookmark the URL.
//   - Lightweight social-proof: seeing "@friend posted 2 hours ago"
//     is a stronger return-visit cue than a generic global trending.
//   - Tappable: each card opens the publisher's profile at the
//     specific video. No state plumbing — pure routing.
//
// Empty / logged-out states:
//   - Logged-out users see nothing (strip self-hides). Their /feed
//     experience is identical to /.
//   - Logged-in users with no follows or no recent activity see
//     nothing. The strip is opt-in by data.
const RECENT_WINDOW_HOURS = 48;
const MAX_CARDS = 6;

const FollowingRecentStrip = ({ user }) => {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    if (!user) { setVideos([]); return; }
    let cancelled = false;
    (async () => {
      try {
        // apiClient picks up the auth token from localStorage; no need
        // to thread it through props.
        const res = await api.get('/feed/following?limit=20');
        const cutoff = Date.now() - RECENT_WINDOW_HOURS * 60 * 60 * 1000;
        const recent = (Array.isArray(res.data) ? res.data : [])
          .filter(v => v.created_at && new Date(v.created_at).getTime() >= cutoff)
          .slice(0, MAX_CARDS);
        if (!cancelled) setVideos(recent);
      } catch (err) {
        if (!cancelled) setVideos([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user || videos.length === 0) return null;

  return (
    <div
      className="w-full max-w-7xl mx-auto px-3 sm:px-4 pt-4"
      data-testid="following-recent-strip"
    >
      <div className="flex items-center gap-2 mb-2">
        <Clock className="h-3.5 w-3.5 text-emerald-400" />
        <h3 className="text-gray-300 text-[11px] uppercase tracking-wider font-medium">
          Recently from people you follow
        </h3>
        <span className="text-gray-500 text-[10px]">· last {RECENT_WINDOW_HOURS}h</span>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        {videos.map((v) => {
          const username = v.producer_username || v.username || v.owner_username;
          const minsAgo = Math.max(1, Math.floor((Date.now() - new Date(v.created_at).getTime()) / 60000));
          const ago = minsAgo < 60
            ? `${minsAgo}m ago`
            : minsAgo < 1440
              ? `${Math.floor(minsAgo / 60)}h ago`
              : `${Math.floor(minsAgo / 1440)}d ago`;
          return (
            <Link
              key={v.id}
              to={`/${username}?video=${v.id}`}
              className="group shrink-0 w-40 sm:w-48 rounded-lg bg-gray-900/60 hover:bg-gray-800/80 border border-gray-800 hover:border-emerald-500/40 transition-all overflow-hidden"
              data-testid={`following-strip-card-${v.id}`}
            >
              <div className="relative aspect-video bg-black flex items-center justify-center">
                {v.thumbnail_url ? (
                  <img
                    src={v.thumbnail_url}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 to-black" />
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                  <Play className="h-8 w-8 text-white fill-white" />
                </div>
              </div>
              <div className="p-2">
                <p className="text-white text-[12px] font-medium leading-snug line-clamp-2 mb-1">
                  {v.title}
                </p>
                <p className="text-gray-400 text-[10px]">
                  <span className="text-emerald-400">@{username}</span>
                  <span className="mx-1">·</span>
                  {ago}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default FollowingRecentStrip;
